import { useCallback, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserProfile,
  NotificationSettings,
  updateNotificationSettings,
} from '../../services/users';
import {
  createHarborRunnerToken,
  CreatedHarborRunnerToken,
  HarborRunnerToken,
  listHarborRunnerTokens,
  revokeHarborRunnerToken,
} from '../../services/harborRunnerTokens';
import { logGlobalError } from '../../utils/globalErrorLogger';

export interface SettingsViewModel {
  busy: boolean;
  error: string | null;
  displayName: string | null;
  email: string | null;
  expertId: string | null;
  notifyOnApproved: boolean;
  notifyOnChangesRequested: boolean;
  saving: boolean;
  accessTokens: HarborRunnerToken[];
  accessTokensLoading: boolean;
  accessTokenCreating: boolean;
  accessTokenCreated: CreatedHarborRunnerToken | null;
  revokingAccessTokenId: string | null;
  initialize: () => Promise<void>;
  setNotifyOnApproved: (value: boolean) => Promise<void>;
  setNotifyOnChangesRequested: (value: boolean) => Promise<void>;
  createAccessToken: (label: string, expiresInDays: number) => Promise<void>;
  revokeAccessToken: (tokenId: string) => Promise<void>;
}

export function useSettingsViewModel(): SettingsViewModel {
  const { currentUser } = useAuth();
  const [busy, setBusy] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [expertId, setExpertId] = useState<string | null>(null);
  const [notifyOnApproved, setNotifyOnApprovedState] = useState<boolean>(true);
  const [notifyOnChangesRequested, setNotifyOnChangesRequestedState] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [accessTokens, setAccessTokens] = useState<HarborRunnerToken[]>([]);
  const [accessTokensLoading, setAccessTokensLoading] = useState<boolean>(true);
  const [accessTokenCreating, setAccessTokenCreating] = useState<boolean>(false);
  const [accessTokenCreated, setAccessTokenCreated] = useState<CreatedHarborRunnerToken | null>(null);
  const [revokingAccessTokenId, setRevokingAccessTokenId] = useState<string | null>(null);

  const initialize = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (!currentUser) {
        return;
      }
      const [profile, tokens] = await Promise.all([
        getUserProfile(currentUser.uid),
        listHarborRunnerTokens(),
      ]);
      setDisplayName(profile?.displayName ?? currentUser.displayName ?? null);
      setEmail(profile?.email ?? currentUser.email ?? null);
      setExpertId(profile?.expertId ?? null);
      setNotifyOnApprovedState(profile?.notifyOnApproved !== false);
      setNotifyOnChangesRequestedState(profile?.notifyOnChangesRequested !== false);
      setAccessTokens(tokens);
    } catch (e: any) {
      setError(e?.message || 'Failed to load settings');
      logGlobalError(e, {
        context: 'Settings.initialize',
        metadata: { uid: currentUser?.uid ?? null },
      });
    } finally {
      setBusy(false);
      setAccessTokensLoading(false);
    }
  }, [currentUser]);

  const createAccessToken = useCallback(
    async (label: string, expiresInDays: number) => {
      if (!currentUser) return;
      setAccessTokenCreating(true);
      setError(null);
      setAccessTokenCreated(null);
      try {
        const created = await createHarborRunnerToken(label, expiresInDays);
        setAccessTokenCreated(created);
        setAccessTokens((previous) => [
          {
            id: created.tokenId,
            label: created.label,
            createdAt: created.createdAt,
            expiresAt: created.expiresAt,
            revokedAt: null,
            lastFour: created.token.slice(-4),
            status: 'active',
            scopes: created.scopes,
          },
          ...previous,
        ]);
      } catch (e: any) {
        setError(e?.message || 'Failed to create access token');
        logGlobalError(e, {
          context: 'Settings.createAccessToken',
          metadata: { uid: currentUser.uid },
        });
      } finally {
        setAccessTokenCreating(false);
      }
    },
    [currentUser],
  );

  const revokeAccessToken = useCallback(
    async (tokenId: string) => {
      if (!currentUser) return;
      setRevokingAccessTokenId(tokenId);
      setError(null);
      try {
        await revokeHarborRunnerToken(tokenId);
        setAccessTokens((previous) => previous.map((token) => (
          token.id === tokenId
            ? { ...token, status: 'revoked', revokedAt: new Date().toISOString() }
            : token
        )));
      } catch (e: any) {
        setError(e?.message || 'Failed to revoke access token');
        logGlobalError(e, {
          context: 'Settings.revokeAccessToken',
          metadata: { uid: currentUser.uid, tokenId },
        });
      } finally {
        setRevokingAccessTokenId(null);
      }
    },
    [currentUser],
  );

  const persist = useCallback(
    async (patch: Partial<NotificationSettings>) => {
      if (!currentUser) return;
      setSaving(true);
      setError(null);
      try {
        await updateNotificationSettings(currentUser.uid, patch);
      } catch (e: any) {
        setError(e?.message || 'Failed to save settings');
        logGlobalError(e, {
          context: 'Settings.persist',
          metadata: { uid: currentUser.uid, fields: Object.keys(patch) },
        });
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [currentUser],
  );

  const setNotifyOnApproved = useCallback(
    async (value: boolean) => {
      const prev = notifyOnApproved;
      setNotifyOnApprovedState(value);
      try {
        await persist({ notifyOnApproved: value });
      } catch {
        setNotifyOnApprovedState(prev);
      }
    },
    [notifyOnApproved, persist],
  );

  const setNotifyOnChangesRequested = useCallback(
    async (value: boolean) => {
      const prev = notifyOnChangesRequested;
      setNotifyOnChangesRequestedState(value);
      try {
        await persist({ notifyOnChangesRequested: value });
      } catch {
        setNotifyOnChangesRequestedState(prev);
      }
    },
    [notifyOnChangesRequested, persist],
  );

  return {
    busy,
    error,
    displayName,
    email,
    expertId,
    notifyOnApproved,
    notifyOnChangesRequested,
    saving,
    accessTokens,
    accessTokensLoading,
    accessTokenCreating,
    accessTokenCreated,
    revokingAccessTokenId,
    initialize,
    setNotifyOnApproved,
    setNotifyOnChangesRequested,
    createAccessToken,
    revokeAccessToken,
  };
}
